import { i18n } from "i18n";

import { BasicInfo } from "models";

import { IAccessibilityContent } from "pages/MainPage/MainPage";
import { IBasicInfoConverterContent } from "./converters";
import { IBasicInfoViewModel } from "./viewModels";
import { FEATURE_FLAGS } from "shared-services";
import { LDFlagSet } from "launchdarkly-react-client-sdk";

export const BASIC_INFO_DETAILS_ARRAYS: (keyof IBasicInfoViewModel)[] = [
  "locations",
];

declare type DetailsMap = {
  fieldName: keyof IBasicInfoViewModel;
  contentName: keyof IBasicInfoSectionContent;
};

declare type DetailsHintMap = {
  fieldName: keyof IBasicInfoViewModel;
  hintContentName: keyof IBasicInfoSectionContent;
  i18nKey?: string;
  isHintVisible?: (basicInfo: BasicInfo) => boolean;
};

export interface IBasicInfoSectionContent
  extends IBasicInfoConverterContent,
    IAccessibilityContent {
  detailsSectionName: string;
  detailsSectionShowMore: string;
  detailsSectionShowLess: string;
  detailsLocationName: string;
  detailsDisabilitiesName: string;
  detailsPregnancyName: string;
  detailsActualAgeName: string;
  detailsPlayingAgeName: string;
  detailsLicenseRequiredName: string;
  detailsHeightName: string;
  detailsNationalitiesName: string;
  detailsMembershipsName: string;
  detailsAppearancesName: string;
  detailsEyeColorName: string;
  detailsHairColorName: string;
  detailsHairLengthName: string;
  detailsFacialHairName: string;
  detailsVoiceQualityName: string;
  detailsVoiceCharacterName: string;
  detailsVoiceStyle: string;
  detailsVocalRangeName: string;
  detailsVocalAgeName: string;
  detailsChestSizeName: string;
  detailsWaistSizeName: string;
  detailsHipsSizeName: string;
  detailsInsideLegSizeName: string;
  detailsInsideArmsSizeName: string;
  detailsWeightName: string;
  detailsCollarSizeName: string;
  detailsHatSizeName: string;
  detailsShoeSizeName: string;
  detailsDressSizeName: string;
  detailsTwinName: string;
  detailsGenderName: string;
  detailsGenderNameHint: string;
  detailsDisabilitiesNameHint: string;
  detailsAlternativeText: string;
  detailsListAlternativeText: string;
  performerContacted: string;
  detailsEthnicityName: string;
  detailsMakeUp: string;
  detailsVisasAndPermits: string;
}

export const BASIC_INFO_VIEW_TO_DETAILS_MAPPING: DetailsMap[] = [
  { fieldName: "locations", contentName: "detailsLocationName" },
  { fieldName: "visasAndPermits", contentName: "detailsVisasAndPermits" },
  { fieldName: "genders", contentName: "detailsGenderName" },
  { fieldName: "disabilities", contentName: "detailsDisabilitiesName" },
  { fieldName: "actualAge", contentName: "detailsActualAgeName" },
  { fieldName: "playingAge", contentName: "detailsPlayingAgeName" },
  { fieldName: "licenseRequired", contentName: "detailsLicenseRequiredName" },
  { fieldName: "height", contentName: "detailsHeightName" },
  { fieldName: "nationalities", contentName: "detailsNationalitiesName" },
  { fieldName: "ethnicity", contentName: "detailsEthnicityName" },
  { fieldName: "memberships", contentName: "detailsMembershipsName" },
  { fieldName: "appearances", contentName: "detailsAppearancesName" },
  { fieldName: "eyeColor", contentName: "detailsEyeColorName" },
  { fieldName: "hairColor", contentName: "detailsHairColorName" },
  { fieldName: "hairLength", contentName: "detailsHairLengthName" },
  { fieldName: "facialHair", contentName: "detailsFacialHairName" },
  { fieldName: "voiceStyle", contentName: "detailsVoiceStyle" },
  { fieldName: "vocalRange", contentName: "detailsVocalRangeName" },
  { fieldName: "vocalAge", contentName: "detailsVocalAgeName" },
  { fieldName: "chestSize", contentName: "detailsChestSizeName" },
  { fieldName: "waistSize", contentName: "detailsWaistSizeName" },
  { fieldName: "hipsSize", contentName: "detailsHipsSizeName" },
  { fieldName: "insideLegSize", contentName: "detailsInsideLegSizeName" },
  { fieldName: "insideArmSize", contentName: "detailsInsideArmsSizeName" },
  { fieldName: "weight", contentName: "detailsWeightName" },
  { fieldName: "collarSize", contentName: "detailsCollarSizeName" },
  { fieldName: "hatSize", contentName: "detailsHatSizeName" },
  { fieldName: "shoeSize", contentName: "detailsShoeSizeName" },
  { fieldName: "dressSize", contentName: "detailsDressSizeName" },
  { fieldName: "hasTwin", contentName: "detailsTwinName" },
  { fieldName: "makeUp", contentName: "detailsMakeUp" },
];

export const BASIC_INFO_VIEW_HINT_MAPPING: DetailsHintMap[] = [
  {
    fieldName: "genders",
    hintContentName: "detailsGenderNameHint",
    isHintVisible: (basicInfo: BasicInfo) => !basicInfo.isVisibleGender,
  },
  {
    fieldName: "disabilities",
    hintContentName: "detailsDisabilitiesNameHint",
    isHintVisible: (basicInfo: BasicInfo) => !basicInfo.isVisibleDisability,
  },
  {
    fieldName: "visasAndPermits",
    hintContentName: "detailsVisasAndPermits",
    i18nKey: "performer:profile.basicInfo.hint.visasAndPermits",
    isHintVisible: (basicInfo: BasicInfo) =>
      !basicInfo.isVisibleVisasAndPermits,
  },
];

export const isVisibleField = (
  filedName: keyof IBasicInfoViewModel,
  launchDarklyFlags?: LDFlagSet
): boolean => {
  let isVisible = true;
  if (filedName === "makeUp") {
    isVisible =
      launchDarklyFlags?.[FEATURE_FLAGS.ProfileMakeupDetails] ?? false;
  } else if (filedName === "visasAndPermits") {
    isVisible = launchDarklyFlags?.[FEATURE_FLAGS.VisasAndPermits] ?? false;
  }

  return isVisible;
};

export const getHintResolver =
  (
    basicInfo: BasicInfo,
    content: IBasicInfoSectionContent,
    isViewMode: boolean
  ) =>
  (fieldName: keyof IBasicInfoViewModel): string | undefined => {
    const { t } = i18n;
    let hint: string | undefined;

    if (!isViewMode) {
      const hintMapping = BASIC_INFO_VIEW_HINT_MAPPING.find(
        (map) => map.fieldName === fieldName
      );

      if (hintMapping) {
        const { isHintVisible, hintContentName, i18nKey } = hintMapping;
        const hintText = i18nKey ? t(i18nKey) : content[hintContentName];

        if (isHintVisible) {
          hint = isHintVisible(basicInfo) ? hintText : undefined;
        } else {
          hint = hintText;
        }
      }
    }

    return hint;
  };
